/* eslint-disable max-lines */
/* eslint-disable max-len */
import xss from 'xss'
import { whiteList as defaultCssWhiteList } from 'cssfilter'
import moment from 'moment'
import anchorme from 'anchorme'
import Vue from 'vue'
import Toasted from 'vue-toasted'
import confetti from 'canvas-confetti'
import * as Sentry from '@sentry/vue'
import filterProfaneWords from 'storybook-utils/profanity-filter/filterProfaneWords'
import EventBus from '@/eventBus'
import Config from '@/config'
import Constant from '@/constant'
import { flags } from '@/utils/abtest.utils'

// services
import cookie from '@/services/cookie'

import qnaUtils from './utils/qna'
import { isIOS } from './utils/dom.utils'
import store from './vuex/store'
import eventCategories from './constant/eventCategories'

import shuffleArrayWithSeed from '@/utils/shuffleArrayWithSeed'
import removeInitialLoader from '@/utils/removeInitialLoader'
import { isPptOnlineAddin } from '@/utils/ppt-online.utils'
import { getParamValue } from './utils/url.utils'
import {
  SET_CURRENT_SLIDE_MODE,
  FULL_SCREEN_MODE,
  WINDOWED_MODE,
  EDIT_MODE,
} from '@/vuex/types'
import mixpanelUtils from './utils/mixpanel.utils'
import router from './router'

import syncTime from './utils/syncTime.utils'
import promotionCodeUtils from './utils/promotionCodeUtils'
import VueToastedUtils from '@/utils/vue-toasted.utils'
import { clearAllCachedData } from './vuex/modules/loading-page/loading-page.utils'

Vue.use(Toasted)

const getHoursMinutesSeconds = (seconds) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secondsLeft = Math.floor(seconds % 60)
  return {
    hours,
    minutes,
    secondsLeft,
  }
}

const getUploadFileStatus = (isUploadFileCalled, isUploadingFile, aiChatbotImportSlidesError, aiChatbotUploadedFile) => {
  if (isUploadFileCalled || isUploadingFile) {
    return 'loading'
  }
  if (aiChatbotImportSlidesError) {
    return 'error'
  }
  const importStatus = aiChatbotUploadedFile?.file_metadata?.import_status
  if (importStatus === 'processing') {
    return 'loading'
  }
  if (importStatus === 'failed') {
    return 'error'
  }
  if (aiChatbotUploadedFile) {
    return 'success'
  }
  return 'loading'
}

export const getFileNameFromURL = (url) => {
  // Tách URL bằng dấu '/'
  const parts = url.split('/')

  // Lấy phần tử cuối cùng trong mảng parts
  const lastPart = parts[parts.length - 1]

  // Tách tên tệp từ phần tử cuối cùng bằng dấu '?'
  return lastPart.split('?')[0]
}

const setupCapterraTracking = () => {
  const capterraVid = '2135517'
  const capterraVkey = '8152b6f0106e9871b0b4756334258635'
  const capterraPrefix = document.location.protocol === 'https:' ? 'https://ct.capterra.com' : 'http://ct.capterra.com'

  const ct = document.createElement('script')
  ct.async = true
  ct.type = 'text/javascript'
  ct.src = `${capterraPrefix}/capterra_tracker.js?vid=${capterraVid}&vkey=${capterraVkey}`

  const s = document.getElementsByTagName('script')[0]

  s.parentNode.insertBefore(ct, s)
}

const scrollToElement = (element, options) => {
  if (!element || !options) return

  if (!element.scrollTo) {
    element.scrollTop = options.top
    return
  }

  element.scrollTo(options)
}

const formatNumber = (number) => { // util.spec.js
  if (!number) return 0

  if (!store.state.presentation.language) return number

  return parseFloat(number).toLocaleString(store.state.presentation.language)
}

const move = (array, from, to) => {
  array.splice(to, 0, array.splice(from, 1)[0])
}

const migrateTokenCookie = () => {
  const token = cookie.getValue('ahaToken')

  if (token === '') {
    cookie.deleteValue('ahaToken')
  }
}

const handleGoogleSlideToken = (token) => {
  window.localStorage.setItem('ahaToken', token)
}

const setCookieToken = (token) => {
  // Tha giet nham con hon bo sot
  migrateTokenCookie()

  cookie.setTokenValue('ahaToken', token)
  Vue.http.headers.common.Authorization = `Bearer ${token}`
  if (Config.integrationPlatform === 'GoogleSlide') {
    handleGoogleSlideToken(token)
  }
}

const cleanUserCookies = () => {
  // Disable welcome popup when logout
  cookie.deleteValue('aha-new-user')
  cookie.deleteValue('aha-user-id')
}

const deleteTokenCookie = () => {
  cookie.deleteTokenValue('ahaToken')
  Vue.http.headers.common.Authorization = ''
}

const logout = (currentUser = {}, redirectUrl = '') => {
  deleteTokenCookie()
  cleanUserCookies()
  cookie.deleteRootDomainCookie('aha_plan_key', { sameSite: 'lax' })
  localStorage.removeItem('ahaToken')
  localStorage.removeItem('fonts-manager')
  localStorage.removeItem('isVisibleBanner')
  promotionCodeUtils.removePromotionCode()
  mixpanelUtils.reset()
  clearAllCachedData()
  if (Config.region === Constant.euRegion && (currentUser.identifyProvider === 'skoletube' || !currentUser.identifyProvider)) {
    window.location.href = Config.skoletubeHomepage
    return
  }
  sessionStorage.removeItem('isAdmin')

  // An explicit redirect target from the caller wins over the platform-specific
  // defaults below (e.g. "Switch account" sends the user to the login page with
  // the device page queued as the post-login redirect).
  if (redirectUrl) {
    window.location.href = redirectUrl
    return
  }

  if (sessionStorage.getItem('zoomApp') == 'true') {
    router.push({ name: 'ZoomApp' })
    return
  }

  // need to reload the browser
  // so all component data and the store will be cleared
  if (store.state.integrationPlatform === 'GoogleSlide') {
    router.push({ name: 'Login' })
  } else if (Config.isEnableAddinPowerpoint && Config.powerpointOAuthClientId
    && (flags.canUseOauthLogin || isPptOnlineAddin())) {
    // PowerPoint add-in signs back in through the OAuth device-grant page, not the
    // standard login form (the add-in webview can't complete login itself).
    window.location.href = `${window.location.origin}/pages/oauth?from=logout`
  } else {
    window.location.href = `${window.location.origin}/pages/login?from=logout`
  }
}

const syncPlanKeyCookie = (planType) => {
  if (!planType || typeof planType !== 'string') return

  cookie.setRootDomainCookie('aha_plan_key', planType, {
    expires: 30,
    sameSite: 'lax',
    secure: true,
  })
}

// wrapper to check for filteringProfanity first
// so other places don't need to check for state
const filterProfaneWordsWrapper = (text) => {
  return store.state.presentation && store.state.presentation.filteringProfanity
    ? filterProfaneWords(text)
    : text
}
/**
** remove recaptcha's backdrop so user cannot click outside to close challenge
** inspired by another method: https://stackoverflow.com/a/44984658
** call this method right after recaptcha.execute()
*/
const removeRecaptchaCover = () => {
  const recaptchaIframes = document.querySelectorAll('[src*="recaptcha.net/recaptcha/api2/bframe"]')
  const lastRenderedIframe = recaptchaIframes[recaptchaIframes.length - 1]
  if (lastRenderedIframe) {
    const backdrop = lastRenderedIframe.parentNode
      && lastRenderedIframe.parentNode.previousElementSibling
    if (backdrop) backdrop.remove()
  }
}

const preloadImage = (item) => {
  if (!item || item.url.indexOf('googleusercontent') !== -1) return null
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = (() => {
      item.error = false
      resolve(item)
    })
    img.onerror = (() => {
      item.error = true
      resolve(item)
    })
    img.src = item.url
  })
}

const preloadVideo = (item) => {
  if (isIOS()) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.src = item.url
    video.load()

    video.addEventListener('load', () => {
      item.error = false
      resolve(item)
    })

    video.addEventListener('error', () => {
      item.error = true
      reject(item)
    })
  })
}

const sleep = (ms) => {
  // eslint-disable-next-line promise/avoid-new
  return new Promise((resolve) => {
    window.setTimeout(() => {
      resolve(false)
    }, ms)
  })
}
window.sleepPromise = sleep

export function ga4() {
  if (Config.region === Constant.euRegion) {
    return
  }
  try {
    if (!window.dataLayer || !window.dataLayer.push) return
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments)
  } catch (e) {
    Sentry.captureException(e)
  }
}

const dispatchShowToastAction = (type, name) => {
  if (!name) {
    // eslint-disable-next-line no-console
    console.warn('Name is required to distinguish toast. The name should be unique')
    return
  }

  store.dispatch('showToastAction', {
    type,
    name,
  })
}

// Timed export-outcome events. time_event() is started for both at request time;
// whichever outcome fires carries mixpanel's automatic $duration (seconds) =
// end-to-end export time (the other timer is simply abandoned).
const EXPORT_COMPLETED_EVENT = 'Results menu > Export > Completed'
const EXPORT_FAILED_EVENT = 'Results menu > Export > Failed'
const exportOutcomeLabel = (exportType) => ({ image: 'Image', pdf: 'PDF', excel: 'Excel' }[exportType] || 'PDF')

// AHA-46264: which renderer produced an export, so the timed completion/failure
// events can be sliced by flow (worker-pptr vs the Cloudflare Worker). Recorded
// at request time by startExportTimers and read back when the outcome fires;
// null (e.g. Excel, which has neither flow) omits the property.
const EXPORT_METHOD = { WORKER_PPTR: 'worker-pptr', CLOUDFLARE_WORKER: 'cloudflare-worker' }
let currentExportMethod = null

export default {
  move,
  sleep,
  removeRecaptchaCover,
  cleanUserCookies,
  logout,
  syncPlanKeyCookie,
  formatNumber,
  scrollToElement,
  setupCapterraTracking,
  filterProfaneWordsWrapper,

  filterProfaneWords,
  validateEmail: (email) => { // util.spec.js
    // eslint-disable-next-line
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase())
  },
  getParamValue: (param, url) => { // util.spec.js
    return getParamValue(param, url)
  },

  // truncate string and avoid cuting words unless the first word is longer than the limit
  truncate: (str, limit) => { // util.spec.js
    if (!str) return ''
    if (str.length > limit) {
      str = str.substring(0, limit)
      const lastSpace = str.lastIndexOf(' ')
      if (lastSpace != -1) {
        str = str.substring(0, lastSpace)
      }
      str += '…'
    }
    return str
  },
  landingToPricingRouter(to, from, next) {
    const { query, params } = to
    const { id } = params
    // router from landing site
    if (query.campaign === 'landing_site') {
      next({
        name: 'StripeTransition',
        query: {
          ...query,
          plan: id,
        },
      })
      return
    }
    next()
  },
  wrap: (str, limit) => { // util.spec.js if don't understand this function
    if (!str) return []
    const words = str.split(' ')
    const arr = []
    let text = ''
    words.forEach((item, key) => {
      text += `${item} `
      if (key < words.length - 1) {
        const next = text + words[key + 1]
        if (next.length > limit) {
          arr.push(text)
          text = ''
        }
      } else {
        arr.push(text)
      }
    })
    return arr
  },

  ga4Config: (payload) => {
    ga4('config', Config.ga4Id, payload)
  },

  ga4Set: (property, payload) => {
    ga4('set', property, payload)
  },

  sendCustomEventToGTMAndGA4: (eventName, payload) => {
    if (store.state.isEnableAddinPowerpoint && payload.eventCategory) {
      payload.eventCategory = `PPT > ${payload.eventCategory}`
    }
    const data = {
      eventAction: eventName,
      ...payload,
    }
    ga4('event', eventName, data)
  },

  sendCustomEventToGTM: (eventName, payload) => {
    if (store.state.isEnableAddinPowerpoint && payload.eventCategory) {
      payload.eventCategory = `PPT > ${payload.eventCategory}`
    }
    const data = {
      eventAction: eventName,
      send_to: Config.gtmId,
      ...payload,
    }
    ga4('event', eventName, data)
  },

  /**
   * Sends a custom event to GTM + GA4 + Mixpanel.
   * @deprecated Prefer the `v-aha-emit-action` directive (or `v-aha-click`) to track user behavior
   * directly on the interactive element — it carries reactive payloads and keeps tracking next to the
   * markup. Only fall back to this helper when a directive cannot be attached (non-DOM/programmatic
   * tracking). See `src/directives/emit-action.directive.js` and the `vue-conventions` skill.
   */
  trackGA4AndMixpanel: (eventName, payload) => {
    if (store.state.isEnableAddinPowerpoint && payload.eventCategory) {
      payload.eventCategory = `PPT > ${payload.eventCategory}`
    }

    // Auto-add pricing_version from cookie for sign_up and purchase events
    if (['sign_up', 'purchase'].includes(eventName) && !payload.pricing_version) {
      const pricingVersion = cookie.getValue('aha_pricing_version')

      if (typeof pricingVersion === 'string' && pricingVersion) {
        payload.pricing_version = pricingVersion
      }
    }

    const data = {
      eventAction: eventName,
      ...payload,
    }
    // If Chloe specifically asked to stop sending to GTM, please include your eventName into Constant.stoppedSendingToGoogleTagEvents
    if (Constant.stoppedSendingToGoogleTagEvents.includes(eventName)) {
      // include send_to: 'GTM-PMZC62P' to stop sending to Google Tag Manager
      data.send_to = Config.gtmId
    }
    ga4('event', eventName, data)
    const {
      eventCategory, eventAction, eventLabel, eventValue, ...props
    } = payload

    mixpanelUtils.track(eventAction, { // event_name
      event_category: eventCategory,
      event_label: eventLabel,
      event_value: eventValue,
      ...props,
    })
  },
  /**
 * @deprecated mixpanelTracking method is deprecated. Please use `v-aha-emit-action` directive instead to track user behavior
 */
  mixpanelTracking: (eventCategory = '', eventAction = '', eventLabel = '', eventValue, customProps = {}) => {
    if (store.getters.isBackstage) {
      eventCategory = Constant.eventCategories.PRESENTATION_BACKSTAGE
    }

    if (store.state.isMSTeamsApp) {
      customProps.isMSTeamsApp = true
    }
    if (store.getters['zoomApp/isZoomReady']) {
      customProps.isZoomApp = true
    }
    if (store.state.isEnableAddinPowerpoint) {
      eventCategory = `PPT > ${eventCategory}`
    }
    // mixpanel tracking
    mixpanelUtils.track(eventAction, { // event_name
      event_category: eventCategory,
      event_label: eventLabel,
      event_value: eventValue,
      ...customProps,
    })
  },

  // Start mixpanel's timers for the export attempt. Called when an export is
  // requested; the matching completion/failure event carries the automatic
  // $duration = end-to-end export time.
  // AHA-46264: map "is this the Cloudflare PDF flow?" to the tracked method label.
  exportMethod: (isCloudflarePdf) => (isCloudflarePdf ? EXPORT_METHOD.CLOUDFLARE_WORKER : EXPORT_METHOD.WORKER_PPTR),
  startExportTimers: (exportMethod = null) => {
    // AHA-46264: remember this attempt's flow so the outcome event can report it
    // (reset each attempt — null when not applicable, e.g. Excel).
    currentExportMethod = exportMethod
    mixpanelUtils.timeEvent(EXPORT_COMPLETED_EVENT)
    mixpanelUtils.timeEvent(EXPORT_FAILED_EVENT)
  },

  // Fire the export-completion event (with $duration from startExportTimers).
  // exportType is 'image' | 'pdf' | 'excel'.
  trackExportCompleted: (exportType) => {
    mixpanelUtils.track(EXPORT_COMPLETED_EVENT, {
      event_category: Constant.eventCategories.PRESENTATION_REPORT,
      event_label: exportOutcomeLabel(exportType),
      ...(currentExportMethod ? { export_method: currentExportMethod } : {}),
    })
  },

  // Fire the export-failure event (with $duration = time-to-failure). Covers the
  // async worker-failure path, distinct from the request-time "Request Export PDF
  // > Failed" event that fires when the export POST itself rejects.
  trackExportFailed: (exportType) => {
    mixpanelUtils.track(EXPORT_FAILED_EVENT, {
      event_category: Constant.eventCategories.PRESENTATION_REPORT,
      event_label: exportOutcomeLabel(exportType),
      ...(currentExportMethod ? { export_method: currentExportMethod } : {}),
    })
  },

  // * @param commonConfig { eventCategory?, eventAction?, eventLabel?, eventValue? } - shared configs for the same feature
  //* @param uniqueConfig { eventCategory?, eventAction?, eventLabel?, eventValue? } - unique configs each event

  gaTrackEventFactory: (commonConfig) => {
    return function tracking(uniqueConfig) {
      const {
        eventCategory = '',
        eventAction = '',
        eventLabel = '',
        eventValue = null,
      } = { ...commonConfig, ...uniqueConfig }
      this.mixpanelTracking(eventCategory, eventAction, eventLabel, eventValue)
    }
  },
  supportsTouch: () => 'ontouchstart' in window || navigator.msMaxTouchPoints,
  randomIntFromInterval: (min, max) => {
    // https://stackoverflow.com/a/7228322/4672162
    return Math.floor(Math.random() * (max - min + 1) + min)
  },
  randomColor: () => {
    const index = parseInt(Math.random() * (30 - 0) + 0, 10)
    return Constant.colors[index]
  },

  changeRgbaColor: (color, opacity) => { // util.spec.js
    if (!color) return ''
    // eslint-disable-next-line
    let regex = /[\d\.]+\)$/g;
    if (opacity) {
      color = color.replace(regex, `${opacity})`)
    } else {
      color = color.replace(regex, '0.2)')
    }
    return color
  },

  hexToRgba(hex) { // util.spec.js
    let c
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('')
      if (c.length == 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]]
      }
      c = `0x${c.join('')}`
      return (
        `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},1)`
      )
    }
    return null
  },

  hexToRgb(hex) { // util.spec.js
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
      return r + r + g + g + b + b
    })
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ?
      {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      } :
      null
  },
  getColourLuminance(r, g, b) { // util.spec.js
    // For more info: https://dev.to/alvaromontoro/building-your-own-color-contrast-checker-4j7o
    const a = [r, g, b].map((v) => {
      v /= 255
      return v <= 0.03928 ?
        v / 12.92 :
        ((v + 0.055) / 1.055) ** 2.4
    })
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722
  },

  getContrastRatio(textLuminance, baseLuminance) { // util.spec.js
    // For more info: https://dev.to/alvaromontoro/building-your-own-color-contrast-checker-4j7o
    return textLuminance > baseLuminance ?
      ((baseLuminance + 0.05) / (textLuminance + 0.05)) :
      ((textLuminance + 0.05) / (baseLuminance + 0.05))
  },

  getLabelChart(labelsDefault, slide) {
    const labels = labelsDefault.map((item) => {
      if (!item) return ''
      return item.trim()
    })
    const labelTruncate = []
    const breakLine = 4
    let limitInLine
    let labelFontSize
    const padding = {
      // Setting padding top for show label custom in the top column bar chart
      top: 30,
      // Set all of padding back to 0 and move spacing responsibility to CSS @huongnguyen2152 15.05.2020
      left: 30,
      right: 30,
      bottom: 4,
    }
    let maxBarThickness = 120
    let valueFontSize = 23
    if (labels.length < 4) {
      limitInLine = 26
      labelFontSize = 26
    } else if (labels.length < 5) {
      limitInLine = 20
      labelFontSize = 22
    } else if (labels.length < 7) {
      limitInLine = 18
      labelFontSize = 19
    } else if (labels.length < 9) {
      limitInLine = 16
      labelFontSize = 20
      valueFontSize = 21
    } else if (labels.length < 13) {
      limitInLine = 12
      labelFontSize = 14
      valueFontSize = 17
      maxBarThickness = 100
      // eslint-disable-next-line no-use-before-define
    } else if (defaultLimitLength < 20) {
      limitInLine = 8
      labelFontSize = 8
      valueFontSize = 15
    } else {
      limitInLine = 6
      labelFontSize = 8
      valueFontSize = 15
    }
    let defaultLimitLength = breakLine * limitInLine - 8
    labels.forEach((item) => {
      let str = this.truncate(item, defaultLimitLength)
      str = this.wrap(str, limitInLine)
      labelTruncate.push(str)
    })

    return {
      labels: labelTruncate,
      labelFontSize,
      padding,
      valueFontSize,
      maxBarThickness,
      fontColor: slide.textColour,
    }
  },

  // get PIE CHART labels
  getLabelPieChart(labelsDefault, slide, values, valueInPercentage, showPercentage) {
    const labels = labelsDefault.map((item) => {
      if (!item) return ''
      return item && item.trim()
    })
    const labelTruncate = []
    let breakLine = 4
    let limitInLine
    let labelFontSize
    const padding = {
      top: 65,
      bottom: 65,
      left: 45,
      right: 45,
    }
    let valueFontSize = 21

    // Set limitInline, labelFontSize, valueFontSize, breakLine based on % of smallest piece
    // eslint-disable-next-line prefer-spread
    const smallestPiece = Math.min.apply(Math, valueInPercentage)
    if (labels.length < 4) {
      limitInLine = 45
      labelFontSize = 22
      if (smallestPiece < 19 && smallestPiece > 15) {
        limitInLine = 20
        if (showPercentage) {
          valueFontSize = 19.5
        }
      } else if (smallestPiece <= 15 && smallestPiece > 10) {
        limitInLine = 20
        if (showPercentage) {
          valueFontSize = 15
        }
      } else if (smallestPiece <= 10 && smallestPiece >= 5) {
        limitInLine = 15
        if (showPercentage) {
          valueFontSize = 14
        }
      } else if (smallestPiece < 5) {
        limitInLine = 35
      }
    } else if (labels.length >= 4 && labels.length <= 6) {
      limitInLine = 20
      labelFontSize = 19
      if (smallestPiece < 19 && smallestPiece > 15) {
        limitInLine = 20
        breakLine = 3
        if (showPercentage) {
          valueFontSize = 16.5
        }
      } else if (smallestPiece <= 15 && smallestPiece > 10) {
        limitInLine = 15
        if (showPercentage) {
          valueFontSize = 19
        }
      } else if (smallestPiece <= 10 && smallestPiece >= 5) {
        limitInLine = 15
        breakLine = 3
        if (showPercentage) {
          valueFontSize = 13
        }
      } else if (smallestPiece < 5) {
        breakLine = 3
        if (showPercentage) {
          valueFontSize = 20
        }
      }
    } else if (labels.length > 6) {
      limitInLine = 12
      labelFontSize = 19
      if (smallestPiece < 19 && smallestPiece > 15) {
        if (showPercentage) {
          valueFontSize = 20
        }
      } else if (smallestPiece <= 15 && smallestPiece > 10) {
        limitInLine = 12
        breakLine = 3
        if (showPercentage) {
          valueFontSize = 15
        }
      } else if (smallestPiece >= 5 && smallestPiece <= 10) {
        limitInLine = 15
        breakLine = 3
        if (showPercentage) {
          valueFontSize = 14
        }
      } else if (smallestPiece < 5) {
        limitInLine = 15
        breakLine = 2
        valueFontSize = 21
      }
    }

    // Truncate label
    const defaultLimitLength = breakLine * limitInLine - 8
    labels.forEach((item) => {
      let str = this.truncate(item, defaultLimitLength)
      str = this.wrap(str, limitInLine)
      labelTruncate.push(str)
    })

    // Set padding based on number of label line after truncate (not breakLine as it's based on % smallest piece)
    const labelTruncateLength = []
    let maxLabelLine
    for (let i = 0; i < labelTruncate.length; i++) {
      // Avoid cases when label line > breakLine
      if (labelTruncate[i].length > breakLine) {
        labelTruncate[i].length = breakLine
      }
      labelTruncateLength.push(labelTruncate[i].length)
      // eslint-disable-next-line prefer-spread
      maxLabelLine = Math.max.apply(Math, labelTruncateLength)
      if (maxLabelLine === 3) {
        padding.top = 80
        padding.bottom = 80
      } else if (maxLabelLine >= 4) {
        padding.top = 117
        padding.bottom = 117
      }
    }

    return {
      labels: labelTruncate,
      labelFontSize,
      padding,
      valueFontSize,
      fontColor: slide.textColour,
    }
  },

  formatPresentationReconect(data) {
    const _sortOptionsByDate = (a, b) => {
      return moment(a.updatedAt).diff(b.updatedAt)
    }
    data.slides = data.Slides
    const slideTypes = {}
    const allTypeSections = store.getters['slideTypeList/getSlideTypeList']
    allTypeSections.forEach((item) => {
      item.slideTypes.forEach((typeObj) => {
        slideTypes[typeObj.type] = typeObj
      })
    })

    delete data.Slides
    if (!data.language) {
      data.language = 'en'
    }

    if (data.slides) {
      data.slides.forEach((item) => {
        try {
          if (item.additionalFields) {
            item.additionalFields = JSON.parse(item.additionalFields)
          } else {
            item.additionalFields = []
          }
        } catch (error) {
          item.additionalFields = []
          Sentry.captureException(error)
        }
        if (item.type) {
          item.typeObj = slideTypes[item.type]
        }
        item.SlideOptions.sort((a, b) => {
          if (item.type == 'openEnded') {
            return _sortOptionsByDate(a, b)
          }
          return a.order - b.order
        })
        // if (item.hints && item.hints.length) {
        // }
        item.hints.sort((a, b) => {
          return a.order - b.order
        })
      })

      data.slides.sort((a, b) => {
        return a.order - b.order
      })
    }

    data.slides.sort((a, b) => {
      return a.order - b.order
    })

    return data
  },

  getIndexSlide(slides, id) { // util.spec.js
    return slides.findIndex((item) => {
      return item.id == id
    })
  },

  isURL(str) { // util.spec.js
    const pattern = /^(https?:\/\/)?([\w.-]+)(\.[a-z]{2,6})(:[\d]+)?(\/[\w .-]*)*\/?(\?[=&\w.%-]*)*$/
    return pattern.test(str)
  },

  toYouTubeLink(str) { // util.spec.js
    const idIndex = str.indexOf('youtu.be/')
    if (idIndex == -1) {
      return str
    }
    return str.replace('youtu.be/', 'youtube.com/watch?v=')
  },

  uploadSlide(slideActive, presentation) {
    const index = presentation.slides.findIndex((item) => {
      return item.id === slideActive.id
    })

    if (index !== -1) {
      presentation.slides[index].SlideOptions = slideActive.SlideOptions
      if (presentation.slides[index].activeTab !== slideActive.activeTab) {
        presentation.slides[index].activeTab = slideActive.activeTab
      }
      if (
        presentation.slides[index].addCorrectOption !==
        slideActive.addCorrectOption
      ) {
        presentation.slides[index].addCorrectOption =
          slideActive.addCorrectOption
      }
      if (presentation.slides[index].backgroundImage !== slideActive.backgroundImage) {
        presentation.slides[index].backgroundImage = slideActive.backgroundImage
        presentation.slides[index].originBackgroundImage = slideActive.originBackgroundImage
        presentation.slides[index].backgroundImageCropperData = slideActive.backgroundImageCropperData
      }
      if (presentation.slides[index].baseColour !== slideActive.baseColour) {
        presentation.slides[index].baseColour = slideActive.baseColour
      }
      if (presentation.slides[index].description !== slideActive.description) {
        presentation.slides[index].description = slideActive.description
      }
      if (
        presentation.slides[index].entriesPerParticipant !==
        slideActive.entriesPerParticipant
      ) {
        presentation.slides[index].entriesPerParticipant =
          slideActive.entriesPerParticipant
      }
      if (presentation.slides[index].hideResult !== slideActive.hideResult) {
        presentation.slides[index].hideResult = slideActive.hideResult
      }
      if (presentation.slides[index].image !== slideActive.image) {
        presentation.slides[index].image = slideActive.image
      }
      if (presentation.slides[index].imageCaption !== slideActive.imageCaption) {
        presentation.slides[index].imageCaption = slideActive.imageCaption
      }
      if (presentation.slides[index].imageType !== slideActive.imageType) {
        presentation.slides[index].imageType = slideActive.imageType
      }

      if (presentation.slides[index].layout !== slideActive.layout) {
        presentation.slides[index].layout = slideActive.layout
      }
      if (presentation.slides[index].limitChoice !== slideActive.limitChoice) {
        presentation.slides[index].limitChoice = slideActive.limitChoice
      }
      if (
        presentation.slides[index].multipleChoice !== slideActive.multipleChoice
      ) {
        presentation.slides[index].multipleChoice = slideActive.multipleChoice
      }
      if (
        presentation.slides[index].multipleCountAnswer !==
        slideActive.multipleCountAnswer
      ) {
        presentation.slides[index].multipleCountAnswer =
          slideActive.multipleCountAnswer
      }
      if (
        presentation.slides[index].openEndedMultipleOption !==
        slideActive.openEndedMultipleOption
      ) {
        presentation.slides[index].openEndedMultipleOption =
          slideActive.openEndedMultipleOption
      }
      if (presentation.slides[index].order !== slideActive.order) {
        presentation.slides[index].order = slideActive.order
      }
      if (
        presentation.slides[index].showAllBulletPoints !==
        slideActive.showAllBulletPoints
      ) {
        presentation.slides[index].showAllBulletPoints =
          slideActive.showAllBulletPoints
      }
      if (
        presentation.slides[index].showCorrectOption !==
        slideActive.showCorrectOption
      ) {
        presentation.slides[index].showCorrectOption =
          slideActive.showCorrectOption
      }
      if (
        presentation.slides[index].showPercentage !== slideActive.showPercentage
      ) {
        presentation.slides[index].showPercentage = slideActive.showPercentage
      }
      if (
        presentation.slides[index].stopSubmission !== slideActive.stopSubmission
      ) {
        presentation.slides[index].stopSubmission = slideActive.stopSubmission
      }
      if (presentation.slides[index].subheading !== slideActive.subheading) {
        presentation.slides[index].subheading = slideActive.subheading
      }
      if (presentation.slides[index].textColour !== slideActive.textColour) {
        presentation.slides[index].textColour = slideActive.textColour
      }
      if (presentation.slides[index].title !== slideActive.title) {
        presentation.slides[index].title = slideActive.title
      }
      if (presentation.slides[index].type !== slideActive.type) {
        presentation.slides[index].type = slideActive.type
      }
      if (presentation.slides[index].typeChart !== slideActive.typeChart) {
        presentation.slides[index].typeChart = slideActive.typeChart
      }

      if (presentation.slides[index].typeObj !== slideActive.typeObj) {
        presentation.slides[index].typeObj = slideActive.typeObj
      }

      if (presentation.slides[index].visibility !== slideActive.visibility) {
        presentation.slides[index].visibility = slideActive.visibility
      }
      if (presentation.slides[index].voiceActive !== slideActive.voiceActive) {
        presentation.slides[index].voiceActive = slideActive.voiceActive
      }
      if (presentation.slides[index].youtubeLink !== slideActive.youtubeLink) {
        presentation.slides[index].youtubeLink = slideActive.youtubeLink
      }
    }
    presentation.slides.push()
  },

  timeToString(seconds) {
    // multiply by 1000 because Date() requires miliseconds
    const date = new Date(seconds * 1000)
    let mm = date.getUTCMinutes()
    let ss = date.getSeconds()
    // If you were building a timestamp instead of a duration, you would uncomment the following line to get 12-hour (not 24) time
    // if (hh > 12) {hh = hh % 12;}
    // These lines ensure you have two-digits
    if (mm < 10) {
      mm = `0${mm}`
    }
    if (ss < 10) {
      ss = `0${ss}`
    }
    // This formats your string to HH:MM:SS
    return `${mm}:${ss}`
  },

  getColor(str) {
    function hashCode(string) {
      // java String#hashCode
      let hash = 0
      for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash)
      }
      return hash
    }

    function intToRGB(i) {
      const c = (i & 0x00ffffff).toString(16).toUpperCase()

      return '00000'.substring(0, 6 - c.length) + c
    }

    return `#${intToRGB(hashCode(str))}`
  },
  getLastSlide(slides) {
    if (!slides.length) {
      return ''
    }

    return slides[slides.length - 1].id
  },
  importFont(font) {
    // find font size exist in head
    const { head } = document
    const fonts = head.querySelectorAll('link[rel="stylesheet"]')
    let isExist = false
    fonts.forEach((item) => {
      if (item.href.includes(font)) {
        isExist = true
      }
    })
    if (!isExist) {
      const link = `https://fonts.googleapis.com/css?family=${font}`
      const elLink = document.createElement('link')
      elLink.href = link
      elLink.rel = 'stylesheet'
      document.head.appendChild(elLink)
    }
  },
  setFontSlideV2(font) {
    const oldFonts = Object.keys(Constant.fonts)
    const existOldFont = oldFonts.find((oldFont) => oldFont === font)
    if (existOldFont) {
      font = Constant.fonts[existOldFont].label
    }

    const styleId = 'aha-middle-canvas-font'
    let styleEl = document.getElementById(styleId)
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = styleId
      document.head.appendChild(styleEl)
    }
    styleEl.textContent = `#middle-canvas { font-family: "${font}"; }`
    this.importFont(font)
  },
  setFontSlide(font) {
    if (!font) return
    const data = Constant.fonts[font]
    const headID = document.getElementsByTagName('head')[0]
    const link = document.createElement('link')
    link.type = 'text/css'
    link.rel = 'stylesheet'
    link.href = data.url

    headID.appendChild(link)
    const newStyle = document.createElement('style')
    newStyle.appendChild(
      document.createTextNode(
        `\
    @font-face {\
        font-family: ${data.value
};\
        src: url('${data.url
}');\
    }\
    `,
      ),
    )

    document.head.appendChild(newStyle)

    const el = document.getElementById('middle-canvas')
    if (el) {
      el.style.fontFamily = data.value
    }
  },

  sanitize(text, isAllowedUnformattingTags, makeLinksClickable = true) {
    if (!text) return text
    // Check for potentially malicious script URLs
    // eslint-disable-next-line no-script-url
    if (typeof text === 'string' && text.toLowerCase().indexOf('javascript:') !== -1) {
      return ''
    }

    const options = {
      whiteList: {
        i: [],
        u: [],
        b: [],
        em: [],
        br: [],
        ul: [],
        ol: [],
        li: [],
        h1: [],
        h2: [],
        h3: [],
        h4: [],
        sup: [],
        sub: [],
        strong: [],
        p: ['class'],
        span: ['class', 'style', 'contenteditable', 'data-latex'],
        svg: ['width', 'height', 'viewBox', 'preserveAspectRatio'],
        path: ['d'],
        line: ['x1', 'y1', 'x2', 'y2', 'stroke-width'],
      },
      css: {
        whiteList: {
          ...defaultCssWhiteList,
          position: '/^relative$/',
          top: true,
          bottom: true,
          left: true,
          'vertical-align': true,
        },
      },
    }

    if (isAllowedUnformattingTags) {
      options.whiteList = {
        ...options.whiteList,
        a: ['href', 'class', 'target'],
        source: ['src', 'type'],
        iframe: ['src', 'width', 'height', 'frameborder'],
        video: ['width', 'height', 'controls', 'autoplay'],
        audio: ['width', 'height', 'controls', 'autoplay'],
      }
    }

    const sanitizedText = xss(text, options)

    if (!store.state.presentation.showHyperLink || !isAllowedUnformattingTags) {
      return sanitizedText
    }
    if (makeLinksClickable) {
      return anchorme(sanitizedText, {
        attributes: [
          {
            name: 'target',
            value: 'blank',
          },
          {
            name: 'class',
            value: 'text-hyper-link',
          },
        ],
      })
    }
    return sanitizedText
  },

  sortOptionsByDate(a, b) {
    return moment(a.updatedAt).diff(b.updatedAt)
  },

  getOS() {
    let detectOS = 'Unknown OS'

    if (navigator.appVersion.indexOf('Win') != -1) { detectOS = 'Windows' }

    if (navigator.appVersion.indexOf('Mac') != -1) { detectOS = 'MacOS' }

    if (navigator.appVersion.indexOf('Linux') != -1) { detectOS = 'Linux' }

    return detectOS
  },

  isMacOS() {
    return this.getOS() === 'MacOS'
  },

  updateDataQuestionAnswer(arr, index) {
    let countTopQuestion = 0
    let countAnswer = 0
    let countPinned = 0
    let countQuestionNotAnswered = 0
    let lastQuestion = {}

    if (index == 3) {
      arr.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt)
      })
    }

    if (index == 4 || index == 1 || index == 2) {
      arr.sort((a, b) => {
        return new Date(a.createdAt) - new Date(b.createdAt)
      })
    }

    if (index == 1) {
      arr.sort((a, b) => {
        return b.voteCount - a.voteCount
      })
    }

    if (index == 5) {
      arr.sort((a, b) => {
        return b.answeredTimestamp - a.answeredTimestamp
      })
    }

    if (index == 1) {
      arr.sort((a, b) => {
        return b.pinned - a.pinned
      })
    }

    arr.forEach((item) => {
      if (item.answered) item.status = 'approved'
      if ((!store.state.presentation.isModerationMode && item.status !== 'dismissed') || (store.state.presentation.isModerationMode && item.status === 'approved')) {
        if (!item.answered) {
          countTopQuestion++
        } else {
          countAnswer++
        }
        if (item.pinned && !item.answered) {
          countPinned++
        }
      }
    })

    if (!store.state.presentation.isModerationMode) {
      lastQuestion = qnaUtils._getLastQuestionQA(arr)
      countQuestionNotAnswered = arr.filter((item) => {
        return !item.answered && item.status !== 'dismissed'
      }).length
    } else {
      const arrQuestionApproved = arr.filter((item) => {
        return item.status === 'approved'
      })
      lastQuestion = qnaUtils._getLastQuestionQA(arrQuestionApproved)
      countQuestionNotAnswered = arr.filter((item) => {
        return !item.answered && item.status === 'approved' && item.status !== 'dismissed'
      }).length
    }

    store.state.lastQuestion = lastQuestion
    store.state.questions = arr
    store.state.countTopQuestion = countTopQuestion
    store.state.countPinnedQuestion = countPinned
    store.state.countAnswerQuestion = countAnswer
    store.state.questionCount = countQuestionNotAnswered
  },
  checkTouchScreen() {
    if ('ontouchstart' in document.documentElement) {
      return true
    }
    return false
  },

  censorName(str) {
    if (!str) return ''
    const filteredText = filterProfaneWordsWrapper(str)
    return filteredText.includes('*')
      ? filteredText
      : str.replace(/./gi, '*')
  },

  censorNameLeaderBoard(str) {
    if (!str) return ''
    let filteredText = filterProfaneWords(str.trim())
    if (!filteredText.includes('*')) {
      filteredText = str
    }
    return filteredText
  },

  saveBlockAudience(item) {
    const storageKey = 'blockAudiences'
    let blockAudiences = []
    if (localStorage.getItem(storageKey)) {
      blockAudiences = JSON.parse(localStorage.getItem(storageKey))
    }
    const index = blockAudiences.findIndex((data) => {
      return data == item.audience
    })
    if (item.isCensorName && index === -1) {
      blockAudiences.push(item.audience)
    } else if (!item.isCensorName && index !== -1) {
      blockAudiences.splice(index, 1)
    }
    localStorage.setItem(storageKey, JSON.stringify(blockAudiences))
  },
  numberWithCommas(number, lang) {
    return number.toLocaleString(lang || store.state.presentation.language)
  },
  formatNumberWithCommas(number) {
    if (!number) return 0
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  },
  setCookieToken,
  checkWhiteLabelDomain() {
    if (Config.integrationPlatform === 'GoogleSlide') return false
    if (window.location.hostname === 'localhost') return false
    return window.location.hostname.indexOf(Config.domain) === -1
  },
  setDocumentTitle(title) {
    if (window.location.hostname.indexOf(Config.domain) === -1) {
      document.title = `${title}`
    } else {
      document.title = `${title} | AhaSlides`
    }
  },

  getAhaColor(number) {
    const colors = store.getters['presentationAttributeModule/presentationColorPalette']
    const index = parseInt(number % (colors.length - 1), 10)
    return colors[index]
  },

  // todo: remove when change color palette in leaderboard
  getAhaColorLegacy(number) {
    const index = parseInt(number % (Constant.colors.length - 1), 10)
    return Constant.colors[index]
  },

  isGoogleSlidesLink(str) {
    const ggIndex = str.indexOf('https://docs.google.com/presentation')
    if (ggIndex === -1) {
      return false
    }
    return true
  },

  handleError(value) {
    if (value === 'Presentation has reached slide limit number.') {
      EventBus.$emit('openModalLimitSlides')
      return true
    }
    return false
  },
  showToastInfo(text, uniqName, action, options) {
    if (!text) return
    dispatchShowToastAction('info', uniqName)
    VueToastedUtils.showToast(text, options, {
      hasReturnValue: true,
      className: 'custom-toast__info',
      duration: 3000,
      action: action ? {
        text: '',
        onClick: (e, toastObject) => {
          toastObject.goAway(0)
        },
      } : '',
    })
  },

  showToastSuccess(text, uniqName, action, options) {
    if (!text) return
    dispatchShowToastAction('success', uniqName)
    VueToastedUtils.showToast(text, options, {
      hasReturnValue: true,
      className: 'custom-toast__success',
      duration: 3000,
      action: action ? {
        text: '',
        onClick: (e, toastObject) => {
          toastObject.goAway(0)
        },
      } : '',
    })
  },

  showToastError(text, uniqName, action, options) {
    if (!text) return
    dispatchShowToastAction('error', uniqName)
    VueToastedUtils.showToast(text, options, {
      hasReturnValue: true,
      className: 'custom-toast__error',
      duration: 3000,
      action: action ? {
        text: (typeof action === 'object' && action.text) ? action.text : '',
        onClick: (e, toastObject) => {
          toastObject.goAway(0)
          if (typeof action === 'object' && typeof action.onClick === 'function') action.onClick()
        },
      } : '',
    })
  },

  // 1 => st, 11 => th, 21 => st
  ordinalSuffixOf(i) {
    const j = i % 10
    const k = i % 100
    if (j === 1 && k !== 11) {
      return 'st'
    }
    if (j === 2 && k !== 12) {
      return 'nd'
    }
    if (j === 3 && k !== 13) {
      return 'rd'
    }
    return 'th'
  },
  get_url_extension(url) {
    // eslint-disable-next-line
    return url.split(/[#?]/)[0].split('.').pop().trim()
  },

  roundNumber(number, digits = 2) {
    const k = 10 ** digits
    return (Math.round(number * k) / k).toFixed(digits)
  },

  preloadImage,

  preloadVideo,

  disallowPDFRender() {
    document.body.classList.add('waiting')
  },

  allowPDFRender() {
    document.body.classList.remove('waiting')
  },
  getGACategories(routeName) {
    switch (routeName) {
      case 'Team': return eventCategories.MY_TEAM
      case 'UserProfile': return eventCategories.MY_ACCOUNT
      case 'WhatsNew': return eventCategories.WHATSNEW
      case 'UserPlan': return eventCategories.MY_PLAN
      case 'Upgrade': return eventCategories.PAYMENT
      case 'SharedWithMe': return eventCategories.SHARE_WITH_ME
      case 'TemplateLibrary': return eventCategories.TEMPLATE_LIBRARY
      default:
        return eventCategories.PRESENTATION_LIST
    }
  },

  getShareCodeFromSlug(slug) {
    // Slug can be the shareCode only:
    // e.g: 1618826855842-ejgszp01vu
    if (!slug) return null

    const blocks = slug.split('-')
    if (blocks.length === 1) {
      return blocks[0]
    }

    return `${blocks[blocks.length - 2]}-${blocks[blocks.length - 1]}`
  },

  initConfettiCanvas() {
    const canvas = document.getElementById('firework-canvas')
    if (!canvas) return null
    canvas.confetti =
      canvas.confetti ||
      confetti.create(canvas, {
        resize: true,
      })
    return canvas
  },

  firework() {
    const self = this
    const canvas = this.initConfettiCanvas()
    if (store.state.isScreenCapture) return
    const colors = Constant.colorsHex.filter((item, key) => {
      return item && key < 7
    })

    // eslint-disable-next-line func-names
    const _startFirework = function () {
      let start = null
      let count = 0
      let timeFirework = 250
      // eslint-disable-next-line
      function step(timestamp) {
        if (count === 3) {
          timeFirework = 800
        }
        if (!start) start = timestamp
        const progress = timestamp - start
        if (progress > timeFirework) {
          start = null
          count++
          canvas.confetti({
            startVelocity: 5,
            spread: 360,
            ticks: 200,
            decay: 0.98,
            particleCount: 80,
            shapes: ['circle'],
            colors,
            zIndex: 100,
            origin: {
              x: Math.random(),
              // since they fall down, start a bit higher than random
              y: Math.random() - 0.3,
            },
          })
        }
        self.myReq = window.requestAnimationFrame(step)
      }
      self.myReq = window.requestAnimationFrame(step)
    }
    // eslint-disable-next-line func-names
    self.stopFirework = function () {
      if (self.myReq) {
        window.cancelAnimationFrame(self.myReq)
        self.myReq = undefined
      }
    }
    _startFirework()
    // eslint-disable-next-line consistent-return
    return self
  },

  isUserOwnPresentation(user, presentation) {
    if (!user || !presentation) return false
    return user.id === presentation.userId || user.id === presentation.takenOverBy
  },

  checkShowAudienceInfo() {
    return store.getters['slideSettingModule/isShowingParticipantInfo']
  },

  checkShowAudienceFields() {
    return true
  },

  setToColorPalette(item, index) {
    if (item.color) {
      return item.color
    }

    const colorMap = store.getters['presentationAttributeModule/presentationColorPalette']
    const colorMapLength = colorMap.length
    const currentColor = index % colorMapLength
    item.color = colorMap[currentColor]

    return item.color
  },

  preloadOnboardingV5Assets(resources) {
    const images = []
    const videos = []

    resources.forEach((item) => {
      if (item.previewSource.includes('.webm')) {
        videos.push({
          url: item.previewSource,
        })
      } else {
        images.push({
          url: item.previewSource,
        })
      }
    })
    images.forEach((image) => {
      preloadImage(image)
    })

    videos.forEach((video) => {
      preloadVideo(video)
    })
  },

  preloadOnboardingImages() {
    const images = []
    Constant.onboardingOptions.forEach((item) => {
      if (item.formImage) {
        images.push({
          url: item.formImage,
        })
      }
      if (item.optionImage) {
        images.push({
          url: item.optionImage,
        })
      }
      item.benefits.forEach((benefit) => {
        if (benefit.image) {
          images.push({
            url: benefit.image,
          })
        }
      })
    })
    images.forEach((item) => {
      preloadImage(item)
    })
  },

  shuffleArrayWithSeed,

  isPageReloaded() {
    return (window.performance.navigation && window.performance.navigation.type === 1) ||
      window.performance
        .getEntriesByType('navigation')
        .map((nav) => nav.type)
        .includes('reload')
  },

  fromArrayToMap(arr, field) {
    if (!arr || !field) return null
    if (arr.length === 0) return {}
    const obj = {}
    arr.forEach((elem) => {
      obj[elem[field]] = elem
    })
    return obj
  },
  convertBlocksMapToArray(map, keyName) {
    if (!map) return null
    return Object.keys(map)
      .filter((blockId) => !map[blockId].hidden)
      .map((key, index) => ({ [keyName]: key, index, ...map[key] }))
  },
  arrayMove(arr, fromIndex, toIndex) {
    const newArr = [...arr]
    const element = newArr[fromIndex]
    newArr.splice(fromIndex, 1)
    newArr.splice(toIndex, 0, element)
    return newArr
  },
  removeInitialLoader,
  extractContent(s, space) {
    const div = document.createElement('div')
    div.innerHTML = s
    if (/<img|<video|<audio/g.test(s)) return ''
    if (space) {
      const children = div.querySelectorAll('*')
      for (let i = 0; i < children.length; i++) {
        if (children[i].textContent) { children[i].textContent += ' ' } else { children[i].innerText += ' ' }
      }
    }
    return [div.textContent || div.innerText].toString().trim().replace(/[\n\t\r]/g, '').replace(/ +/g, ' ')
  },
  getCoords(elem) { // crossbrowser version
    const box = elem.getBoundingClientRect()

    const { body } = document
    const docEl = document.documentElement

    const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop
    const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft

    const clientTop = docEl.clientTop || body.clientTop || 0
    const clientLeft = docEl.clientLeft || body.clientLeft || 0

    const top = box.top + scrollTop - clientTop
    const left = box.left + scrollLeft - clientLeft

    return { top: Math.round(top), left: Math.round(left) }
  },

  setCurrentWindowMode() {
    const mode = store.getters.isFullScreenMode
      ? WINDOWED_MODE
      : FULL_SCREEN_MODE
    store.dispatch(SET_CURRENT_SLIDE_MODE, mode)
    if (window.innerWidth > 800) {
      store.dispatch(SET_CURRENT_SLIDE_MODE, EDIT_MODE)
      if (document.fullscreen && document.exitFullscreen) {
        document.exitFullscreen()
      }
    } else {
      store.dispatch(SET_CURRENT_SLIDE_MODE, EDIT_MODE)
    }
  },

  // this is a bit hacky, but still better than badly structured code
  // this will still allow 2 modals to appear at the same time
  // however, only 1 is shown at a time
  toggleDoubleModal(state) {
    const modalsContainer = document.getElementById(('modal-destination'))
    const modals = modalsContainer.querySelectorAll('.aha-modal')
    if (modals.length < 2) return
    if (state === 'inactive') {
      modals[0].classList.add('inactive')
    } else {
      modals[0].classList.remove('inactive')
    }
  },

  getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
    return null
  },
  removeFirstPageCookie() {
    const cookieName = 'ahaFirstPage'
    const myDate = 'Thu, 01 Jan 1970 00:00:01 GMT'
    document.cookie = `${cookieName}=;expires=${myDate};domain=.ahaslides.com;path=/`
    document.cookie = `${cookieName}=;expires=${myDate};domain=.ahaslide.com;path=/`
    document.cookie = `${cookieName}=;expires=${myDate};domain=localhost;path=/`
  },
  getSlideCountObject(presentation) {
    if (!presentation || !presentation.slides) return {}
    const slideCount = {}
    presentation.slides.forEach((slide) => {
      const slideTypeName = this.getSlideCountKey(slide.type, slide.slideType)
      if (slideCount[slideTypeName]) {
        slideCount[slideTypeName]++
      } else {
        slideCount[slideTypeName] = 1
      }
    })
    return slideCount
  },
  getSlideCountKey(type, slideType) {
    const name = {
      spinnerWheel: 'spinnerWheel',
      leaderboard: 'leaderboard',
      imageChoice: 'poll',
      multipleChoice: 'poll',
      openEnded: 'openEnded',
      wordCloud: 'wordCloud',
      scale: 'scales',
      question: 'question',
      ideas: 'brainstorm',
      freestyle: 'content',
      textSlide: 'heading',
      bulletPoints: 'list',
      qrcode: 'qrCode',
      image: 'image',
      youtube: 'youtube',
      document: 'document',
      googleSlides: 'googleSlides',
      diagram: 'diagram',
    }
    if (type === 'pickAnswer') {
      if (slideType === null) return 'pickAnswerSlideCount'
      if (slideType === 'imageChoice') return 'pickImageSlideCount'
      if (slideType === 'typeAnswer') return 'typeAnswerSlideCount'
      if (slideType === 'matchPairs') return 'matchPairsSlideCount'
      if (slideType === 'correctOrder') return 'correctOrderSlideCount'
    } else if (name[type]) {
      return `${name[type]}SlideCount`
    }
    return 'unknownTypeSlideCount'
  },
  getCorrectSlideTypeName(type, slideType) {
    const name = {
      spinnerWheel: 'spinnerWheel',
      leaderboard: 'leaderboard',
      imageChoice: 'poll',
      multipleChoice: 'poll',
      openEnded: 'openEnded',
      wordCloud: 'wordCloud',
      scale: 'scales',
      question: 'q&a',
      ideas: 'brainstorm',
      freestyle: 'content',
      textSlide: 'heading',
      bulletPoints: 'list',
      qrcode: 'qrCode',
      image: 'image',
      youtube: 'youtube',
      document: 'document',
      googleSlides: 'googleSlides',
      ranking: 'ranking',
      pinOnImage: 'pinOnImage',
      ideaBoard: 'ideaBoard',
      embed: 'embed',
      diagram: 'diagram',
    }
    if (type === 'pickAnswer') {
      if (slideType === null) return 'pickAnswer'
      if (slideType === 'imageChoice') return 'pickImage'
      if (slideType === 'typeAnswer') return 'typeAnswer'
      if (slideType === 'matchPairs') return 'matchPairs'
      if (slideType === 'correctOrder') return 'correctOrder'
      if (slideType === 'categorise') return 'categorise'
      return slideType || 'pickAnswer'
    }
    if (type === 'hackathon-slide') {
      return slideType || 'hackathon'
    }
    return name[type] || type || 0
  },
  splitTeamName(name) {
    if (name.length > 30) return `${name.slice(0, 30)}...`
    return name
  },
  getWorkingMode() {
    return 'personal'
    // try {
    //   if (!userId) return 'personal'
    //   const mode = window.localStorage.getItem(`working-mode-${userId}`)
    //   if (!mode) {
    //     window.localStorage.setItem(`working-mode-${userId}`, 'personal')
    //     return 'personal'
    //   }
    //   return mode
    // } catch (e) {
    //   return 'personal'
    // }
  },
  planNormalizer(type) {
    const isEuPlan = /.*-eu$/.test(type)
    // const isOldPlan = /.*-\d+$/.test(type)
    // const oldPlanRegex = /-\d+$/
    if (isEuPlan) return type.replaceAll('-eu', '')
    // if (isOldPlan) return type.replace(oldPlanRegex, '')
    return ''
  },
  isSubscribleOldPlan(type) {
    return /.*-\d+$/.test(type)
  },
  isOneTimePlan(type) {
    const regex = /^one-time-\w+$/
    return regex.test(type)
  },

  async downloadFile(link) {
    const isZoomApp = sessionStorage.getItem('zoomApp') == 'true'
    if (isZoomApp) {
      store.dispatch('zoomApp/downloadFileAction', link)
      return
    }
    const blob = await fetch(link)
      .then((res) => res.blob())
    const url = window.URL.createObjectURL(new Blob([blob]))
    const a = document.createElement('a')
    a.href = url
    a.download = getFileNameFromURL(link)
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
  },
  generateRandomString(length = 10) {
    let result = ''
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789'
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    return result
  },
  capitalizeFirstLetter(string) {
    return `${string.charAt(0).toUpperCase()}${string.slice(1)}`
  },
  getPresenterLocaleDefault() {
    try {
      const languageCode = window.localStorage.getItem('ahaslides-presenter-language')
      if (languageCode) {
        const index = Constant.languageOptions.findIndex((item) => item.language === languageCode)
        if (index !== -1) {
          const languageSelected = Constant.languageOptions[index]
          return languageSelected.language
        }
      }
    } catch (error) {
      Sentry.captureException(error)
    }
    return 'en'
  },
  handleAudienceTrackingAuthentication(isGuest, isNew, provider, redirect) {
    if (Config.region === Constant.euRegion || isGuest || !redirect) return

    const decodedRedirect = decodeURIComponent(redirect)

    if (!decodedRedirect.includes(Config.audienceDomain)) return

    if (isNew) {
      cookie.setValueByDomain('lazyTrackingAudienceSignup', JSON.stringify({
        eventName: 'audience_signup',
        eventAction: 'audience_signup',
        eventCategory: Constant.eventCategories.PRESENTATION_APP,
        method: `with ${provider}`,
      }))
    } else {
      cookie.setValueByDomain('lazyTrackingAudienceLogin', JSON.stringify({
        eventName: 'audience_login',
        eventAction: 'audience_login',
        eventCategory: Constant.eventCategories.PRESENTATION_APP,
        method: `with ${provider}`,
      }))
    }
  },
  isEuOrWhiteLabel() {
    return Config.domain === 'ahaslides.eu' || this.checkWhiteLabelDomain()
  },
  deleteTokenCookie,
  migrateTokenCookie,
  _isQuestionContentValidToGenerateAnswers(question, slideActive) {
    const { SlideOptions: slideOptions } = slideActive
    if (!question) {
      return false
    }
    const realSlideOptions = slideOptions.filter((item) => item && item.title && item.title.length > 0)
    if (realSlideOptions.length >= 4) {
      return false
    }

    const questionWordCounts = question.split(' ').length
    if (questionWordCounts >= 4) {
      return true
    }
    if (questionWordCounts >= 2 && question.endsWith('?')) {
      return true
    }
    return false
  },

  trackReditusConversion(uid, email) {
    if (window.gr && typeof window.gr === 'function') {
      try {
        window.gr('track', 'conversion', { uid, email })
        // send tracking to other platforms for auditing
        mixpanelUtils.track('Create user', { userId: uid, email })
        Sentry.logger.info('Create user ', { userId: uid, email, ...mixpanelUtils.getProperties() })
      } catch (trackingError) {
        Sentry.captureException(trackingError, {
          tags: { component: 'reditus-tracking' },
          extra: { uid, email, message: 'Reditus track conversion call failed' },
        })
      }
    } else {
      Sentry.captureMessage('Reditus: window.gr is not available for conversion tracking.', {
        level: 'warning',
        tags: { component: 'reditus-tracking' },
        extra: { uid, email },
      })
    }
  },

  isAbTestCountryAudienceLimitPricing() {
    if (!store.state.currentUser.country) return false
    const NEW_FREE_PLAN_TEST_GROUP_CONTRIES = [
      'Vietnam',
      'Singapore',
      'Netherlands',
      'Taiwan',
      'Germany',
      'Italy',
      'Philippines',
      'Spain',
      'Malaysia',
      'Mexico',
      'Uzbekistan',
      // add more countries for test group here
    ]
    return NEW_FREE_PLAN_TEST_GROUP_CONTRIES.includes(store.state.currentUser.country)
  },
  getHoursMinutesSeconds,
  getUploadFileStatus,
  ...syncTime,
}
